import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Col,
  Form,
  FormFeedback,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Table,
} from "reactstrap";
import { FieldArray, FormikProvider, useFormik } from "formik";
import { toast } from "react-toastify";
import schema from "./schema";
import type { EmployeeSalaryStructure } from "../types/employee-salary-structure.types";
import {
  createEmployeeSalaryStructure,
  updateEmployeeSalaryStructure,
} from "../services/employeeSalaryStructureService";

type ComponentRow = {
  type: string;
  category: string;
  amount: number | "";
};

const SALARY_COMPONENT_CATEGORIES = [
  { value: "EARNING", label: "Earning" },
  { value: "DEDUCTION", label: "Deduction" },
] as const;

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const _now = new Date();
const SALARY_YEAR_OPTIONS = Array.from(
  { length: 10 },
  (_, i) => _now.getFullYear() - 4 + i,
);

/** Extract YYYY-MM from a YYYY-MM-DD string. */
function toMonthValue(isoOrDate: string | null | undefined): string {
  if (!isoOrDate) return "";
  return String(isoOrDate).slice(0, 7);
}

/** Convert YYYY-MM to YYYY-MM-01 (first day) for effectiveFrom. */
function monthToFirstDay(ym: string): string {
  return ym ? `${ym}-01` : "";
}

/** Convert YYYY-MM to YYYY-MM-DD (last day) for effectiveTo. */
function monthToLastDay(ym: string): string {
  if (!ym) return "";
  const [y, m] = ym.split("-").map(Number);
  const last = new Date(y, m, 0).getDate();
  return `${ym}-${String(last).padStart(2, "0")}`;
}

function MonthYearSelect({
  id,
  value,
  onChange,
  onBlur,
  invalid,
}: {
  id: string;
  value: string;
  onChange: (ym: string) => void;
  onBlur?: () => void;
  invalid?: boolean;
}) {
  const [selMonth, setSelMonth] = useState(value ? value.slice(5, 7) : "");
  const [selYear, setSelYear] = useState(value ? value.slice(0, 4) : "");

  useEffect(() => {
    setSelMonth(value ? value.slice(5, 7) : "");
    setSelYear(value ? value.slice(0, 4) : "");
  }, [value]);

  const emit = (month: string, year: string) => {
    onChange(month && year ? `${year}-${month}` : "");
  };

  const cls = `form-select${invalid ? " is-invalid" : ""}`;

  return (
    <div className="d-flex gap-2">
      <Input
        type="select"
        id={`${id}-month`}
        className={cls}
        value={selMonth}
        onChange={(e) => {
          const m = e.target.value;
          setSelMonth(m);
          emit(m, selYear);
        }}
        onBlur={onBlur}
      >
        <option value="">Month</option>
        {MONTH_NAMES.map((name, i) => (
          <option key={i + 1} value={String(i + 1).padStart(2, "0")}>
            {name}
          </option>
        ))}
      </Input>
      <Input
        type="select"
        id={`${id}-year`}
        className={cls}
        value={selYear}
        onChange={(e) => {
          const y = e.target.value;
          setSelYear(y);
          emit(selMonth, y);
        }}
        onBlur={onBlur}
      >
        <option value="">Year</option>
        {SALARY_YEAR_OPTIONS.map((y) => (
          <option key={y} value={String(y)}>
            {y}
          </option>
        ))}
      </Input>
    </div>
  );
}

const SalaryStructureFormModal = ({
  isOpen,
  onClose,
  employeeId,
  editRecord,
  onSaved,
}: {
  isOpen: boolean;
  onClose: () => void;
  employeeId: number;
  editRecord: EmployeeSalaryStructure | null;
  onSaved: () => void;
}) => {
  const isEdit = !!editRecord;

  const initialValues = useMemo(
    () => ({
      effectiveFrom: toMonthValue(editRecord?.effectiveFrom),
      effectiveTo: toMonthValue(editRecord?.effectiveTo),
      components: (editRecord?.components?.length
        ? editRecord.components.map((c) => ({
            type: c.type,
            category: c.category,
            amount: Number(c.amount),
          }))
        : []) as ComponentRow[],
    }),
    [editRecord],
  );

  const validation = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema: schema,
    onSubmit: async (values) => {
      const components = values.components.map((c) => ({
        type: c.type.trim(),
        category: c.category.trim(),
        amount: Number(c.amount),
      }));
      const effectiveFrom = monthToFirstDay(values.effectiveFrom);
      const effectiveTo =
        values.effectiveTo && String(values.effectiveTo).trim() !== ""
          ? monthToLastDay(values.effectiveTo)
          : null;

      try {
        if (isEdit && editRecord) {
          const res = await updateEmployeeSalaryStructure(
            employeeId,
            editRecord.id,
            { effectiveFrom, effectiveTo, components },
          );
          if (res.success) {
            toast.success(res.message);
            onSaved();
            onClose();
          }
        } else {
          const res = await createEmployeeSalaryStructure(employeeId, {
            effectiveFrom,
            effectiveTo,
            components,
          });
          if (res.success) {
            toast.success(res.message);
            onSaved();
            onClose();
          }
        }
      } catch (err: any) {
        toast.error(
          err?.response?.data?.message ??
            (isEdit
              ? "Failed to update salary structure"
              : "Failed to create salary structure"),
        );
      }
    },
  });

  useEffect(() => {
    if (!isOpen) {
      validation.resetForm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset when modal closes only
  }, [isOpen]);

  const handleClose = () => {
    validation.resetForm();
    onClose();
  };

  return (
    <Modal isOpen={isOpen} toggle={handleClose} centered scrollable size="lg">
      <ModalHeader toggle={handleClose}>
        {isEdit ? "Edit salary structure" : "Add salary structure"}
      </ModalHeader>
      <FormikProvider value={validation}>
        <Form
          onSubmit={(e) => {
            e.preventDefault();
            validation.handleSubmit();
          }}
        >
          <ModalBody>
            <Row className="gy-3">
              <Col md={6}>
                <Label className="form-label" for="ss-effective-from-month">
                  Effective from
                </Label>
                <MonthYearSelect
                  id="ss-effective-from"
                  value={validation.values.effectiveFrom}
                  onChange={(ym) =>
                    validation.setFieldValue("effectiveFrom", ym)
                  }
                  onBlur={() =>
                    validation.setFieldTouched("effectiveFrom", true)
                  }
                  invalid={
                    !!(
                      validation.touched.effectiveFrom &&
                      validation.errors.effectiveFrom
                    )
                  }
                />
                {validation.touched.effectiveFrom &&
                  validation.errors.effectiveFrom && (
                    <FormFeedback type="invalid" className="d-block">
                      {String(validation.errors.effectiveFrom)}
                    </FormFeedback>
                  )}
              </Col>
              <Col md={6}>
                <Label className="form-label" for="ss-effective-to-month">
                  Effective to{" "}
                  <span className="text-muted fw-normal">(optional)</span>
                </Label>
                <MonthYearSelect
                  id="ss-effective-to"
                  value={validation.values.effectiveTo}
                  onChange={(ym) => validation.setFieldValue("effectiveTo", ym)}
                  onBlur={() => validation.setFieldTouched("effectiveTo", true)}
                  invalid={
                    !!(
                      validation.touched.effectiveTo &&
                      validation.errors.effectiveTo
                    )
                  }
                />
                {validation.touched.effectiveTo &&
                  validation.errors.effectiveTo && (
                    <FormFeedback type="invalid" className="d-block">
                      {String(validation.errors.effectiveTo)}
                    </FormFeedback>
                  )}
              </Col>
            </Row>

            <FieldArray name="components">
              {({ push, remove }) => (
                <div className="mt-4">
                  <h6 className="mb-0 text-uppercase text-muted">Components</h6>
                  <p className="text-muted small mb-2">
                    Reducing this employee's net salary below their
                    outstanding advance balance will be blocked. Settle or
                    adjust their advance first.
                  </p>
                  <div className="d-flex align-items-center justify-content-end mb-2">
                    <Button
                      type="button"
                      color="light"
                      size="sm"
                      className="btn-soft-primary"
                      onClick={() =>
                        push({ type: "", category: "EARNING", amount: 0 })
                      }
                    >
                      <i className="ri-add-line align-bottom me-1" />
                      Add row
                    </Button>
                  </div>
                  <div className="table-responsive border rounded">
                    <Table className="align-middle mb-0 table-nowrap">
                      <thead className="table-light">
                        <tr>
                          <th>Type</th>
                          <th style={{ width: "160px" }}>Category</th>
                          <th style={{ width: "140px" }}>Amount</th>
                          <th style={{ width: "56px" }} />
                        </tr>
                      </thead>
                      <tbody>
                        <>
                          {validation.values.components.length === 0 ? (
                            <tr>
                              <td
                                colSpan={4}
                                className="text-muted text-center py-4"
                              >
                                Add at least one component (allowance or
                                deduction).
                              </td>
                            </tr>
                          ) : (
                            validation.values.components.map((_, index) => (
                              <tr key={index}>
                                <td>
                                  <Input
                                    type="text"
                                    name={`components.${index}.type`}
                                    placeholder="e.g. HRA, PF"
                                    value={
                                      validation.values.components[index].type
                                    }
                                    onChange={validation.handleChange}
                                    onBlur={validation.handleBlur}
                                    invalid={
                                      !!(
                                        validation.touched.components?.[index]
                                          ?.type &&
                                        (
                                          validation.errors.components?.[
                                            index
                                          ] as { type?: string } | undefined
                                        )?.type
                                      )
                                    }
                                  />
                                  {validation.touched.components?.[index]
                                    ?.type &&
                                    (
                                      validation.errors.components?.[index] as
                                        | { type?: string }
                                        | undefined
                                    )?.type && (
                                      <div className="invalid-feedback d-block">
                                        {
                                          (
                                            validation.errors.components?.[
                                              index
                                            ] as { type: string }
                                          ).type
                                        }
                                      </div>
                                    )}
                                </td>
                                <td>
                                  <Input
                                    type="select"
                                    className="form-select"
                                    name={`components.${index}.category`}
                                    value={
                                      validation.values.components[index]
                                        .category
                                    }
                                    onChange={validation.handleChange}
                                    onBlur={validation.handleBlur}
                                    invalid={
                                      !!(
                                        validation.touched.components?.[index]
                                          ?.category &&
                                        (
                                          validation.errors.components?.[
                                            index
                                          ] as { category?: string } | undefined
                                        )?.category
                                      )
                                    }
                                  >
                                    <option value="">Select</option>
                                    {SALARY_COMPONENT_CATEGORIES.map((c) => (
                                      <option key={c.value} value={c.value}>
                                        {c.label}
                                      </option>
                                    ))}
                                  </Input>
                                </td>
                                <td>
                                  <Input
                                    type="number"
                                    name={`components.${index}.amount`}
                                    min={0}
                                    step="0.01"
                                    value={
                                      validation.values.components[index]
                                        .amount === ""
                                        ? ""
                                        : validation.values.components[index]
                                            .amount
                                    }
                                    onChange={validation.handleChange}
                                    onBlur={validation.handleBlur}
                                    invalid={
                                      !!(
                                        validation.touched.components?.[index]
                                          ?.amount &&
                                        (
                                          validation.errors.components?.[
                                            index
                                          ] as { amount?: string } | undefined
                                        )?.amount
                                      )
                                    }
                                  />
                                </td>
                                <td className="text-end">
                                  <Button
                                    type="button"
                                    color="link"
                                    className="text-danger p-0"
                                    onClick={() => remove(index)}
                                    aria-label="Remove row"
                                  >
                                    <i className="ri-delete-bin-line fs-18" />
                                  </Button>
                                </td>
                              </tr>
                            ))
                          )}
                        </>
                      </tbody>
                    </Table>
                  </div>
                  {typeof validation.errors.components === "string" && (
                    <div className="text-danger small mt-1">
                      {validation.errors.components}
                    </div>
                  )}
                </div>
              )}
            </FieldArray>
          </ModalBody>
          <ModalFooter>
            <Button type="button" color="light" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              color="primary"
              disabled={validation.isSubmitting}
            >
              {validation.isSubmitting
                ? "Saving..."
                : isEdit
                  ? "Save changes"
                  : "Create"}
            </Button>
          </ModalFooter>
        </Form>
      </FormikProvider>
    </Modal>
  );
};

export default SalaryStructureFormModal;
